// Create-banner wizard — steps, EMPTY_BANNER, supported locales.

import { ROUTES } from 'src/router/const'

export const BANNER_ALIAS_DOMAIN_ROW_ERROR = {
  INVALID_FORMAT: 'invalid_format',
  DUPLICATE: 'duplicate',
  MATCHES_MAIN_URL: 'matches_main_url',
}

/** Tooltip copy for committed additional-domain row validation errors. */
export const BANNER_ALIAS_DOMAIN_ROW_ERROR_TOOLTIPS = {
  [BANNER_ALIAS_DOMAIN_ROW_ERROR.INVALID_FORMAT]: 'Enter a valid domain',
  [BANNER_ALIAS_DOMAIN_ROW_ERROR.DUPLICATE]: 'This domain is already added',
  [BANNER_ALIAS_DOMAIN_ROW_ERROR.MATCHES_MAIN_URL]: 'This domain matches the primary domain',
}

/** Menu-only value for “select all languages”. Not a locale code, and never sent to the API. */
export const AVAILABLE_LOCALES_SELECT_ALL = 'select-all'

export const SUPPORTED_LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'pt-br', label: 'Português (Brasil)' },
  { value: 'bg', label: 'Български' },
  { value: 'ca', label: 'Català' },
  { value: 'hr', label: 'Hrvatski' },
  { value: 'cs', label: 'Čeština' },
  { value: 'cy', label: 'Cymraeg' },
  { value: 'da', label: 'Dansk' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'et', label: 'Eesti' },
  { value: 'eu', label: 'Euskara' },
  { value: 'fi', label: 'Suomi' },
  { value: 'el', label: 'Ελληνικά' },
  { value: 'hu', label: 'Magyar' },
  { value: 'ga', label: 'Gaeilge' },
  { value: 'is', label: 'Íslenska' },
  { value: 'lv', label: 'Latviešu' },
  { value: 'lt', label: 'Lietuvių' },
  { value: 'mk', label: 'Македонски' },
  { value: 'nb', label: 'Norsk Bokmål' },
  { value: 'pl', label: 'Polski' },
  { value: 'ro', label: 'Română' },
  { value: 'sk', label: 'Slovenčina' },
  { value: 'sl', label: 'Slovenščina' },
  { value: 'sq', label: 'Shqip' },
  { value: 'sr', label: 'Српски' },
  { value: 'sv', label: 'Svenska' },
  { value: 'zh', label: '中文' },
  { value: 'zh-hant', label: '繁體中文' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'ar', label: 'العربية' },
  { value: 'ru', label: 'Русский' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'id', label: 'Indonesia' },
  { value: 'ms', label: 'Bahasa Melayu' },
  { value: 'si', label: 'සිංහල' },
  { value: 'ta', label: 'தமிழ்' },
  { value: 'th', label: 'ไทย' },
  { value: 'tr', label: 'Türkçe' },
  { value: 'uk', label: 'Українська' },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'he', label: 'עברית' },
]

export const EMPTY_BANNER = {
  siteId: null,
  url: null,
  siteName: null,
  description: null,
  privacyPolicyUrl: null,
  defaultLocale: 'en',
  regulations: [],
  appearance: null,
  createdAt: null,
  aliasDomains: [],
  catchAllOwner: null,
  scanImmediately: true,
  googleConsentMode: true,
  availableLocales: SUPPORTED_LOCALES.map((locale) => locale.value),
}

/** Banner entity field keys — used in form update/blur emits and field-level error routing. */
export const BANNER_FORM_FIELDS = {
  SITE_NAME: 'siteName',
  URL: 'url',
  ALIAS_DOMAINS: 'aliasDomains',
  DESCRIPTION: 'description',
  DEFAULT_LOCALE: 'defaultLocale',
  AVAILABLE_LOCALES: 'availableLocales',
  PRIVACY_POLICY_URL: 'privacyPolicyUrl',
  SCAN_IMMEDIATELY: 'scanImmediately',
  GOOGLE_CONSENT_MODE: 'googleConsentMode',
  REGULATIONS: 'regulations',
  CATCH_ALL_OWNER: 'catchAllOwner',
}

export const CREATE_BANNER_STEP_NAMES = {
  REGULATIONS: 'regulations',
  BANNER_DETAILS: 'banner-details',
  CONFIGURE: 'configure',
  BANNER_CREATED: 'banner-created',
}

// Ordered tracker steps with display labels. Order determines wizard progression
// and is used by `canAccessCreateBannerStep` for step validation and route generation.
export const CREATE_BANNER_STEPS = [
  {
    key: CREATE_BANNER_STEP_NAMES.REGULATIONS,
    label: 'Regulations',
    icon: 'shield-check',
    path: 'regulations',
    route: ROUTES.CREATE_BANNER_REGULATIONS,
  },
  {
    key: CREATE_BANNER_STEP_NAMES.BANNER_DETAILS,
    label: 'Details',
    icon: 'file-text',
    path: 'details',
    route: ROUTES.CREATE_BANNER_DETAILS,
  },
  {
    key: CREATE_BANNER_STEP_NAMES.CONFIGURE,
    label: 'Configure',
    icon: 'sliders-horizontal',
    path: 'configure',
    route: ROUTES.CREATE_BANNER_CONFIGURE,
  },
  {
    key: CREATE_BANNER_STEP_NAMES.BANNER_CREATED,
    label: 'Done',
    icon: 'rocket',
    path: 'created/:siteId',
    route: ROUTES.CREATE_BANNER_CREATED,
  },
]

/** Maps step keys to their named route - used for navigation and step tracker. */
export const STEP_ROUTE_NAMES = Object.fromEntries(
  CREATE_BANNER_STEPS.map(({ key, route }) => [key, route])
)

/** Reverse lookup: route name → step key. */
export const ROUTE_TO_STEP = Object.fromEntries(
  CREATE_BANNER_STEPS.map(({ key, route }) => [route, key])
)

/** Step-rail visual state for the create-wizard and overview progress card. */
export const STEP_RAIL_STATUS = {
  DONE: 'done',
  ACTIVE: 'active',
  ICON: 'icon',
}
